import { Alert, Autocomplete, Box, Grid, SvgIcon, useTheme } from '@mui/material';
import { FormikProps } from 'formik';
import TextField from '@mui/material/TextField';
import { searchSerialNumbers } from '../../../services/equipment.service';
import { useState } from 'react';
import { getPairingInfoByDeviceId, getPairingInfoByEquipmentId } from '../../../services/pairing.service';
import { Check, Error, Warning } from '@mui/icons-material';
import { searchDeviceIds } from '../../../services/device.service';
import AuthService from '../../../services/auth.service';
import { AppPermissions } from '../../../constants/Permissions';

export interface PairingInput {
  serialNumber: string;
}

interface PairingProps {
  formRef: FormikProps<PairingInput>;
  currentValue: option;
  setCurrentValue;
  // if true, we're looking at the pairing from the perspective of a device.
  // Otherwise, we're looking from the perspective of equipment
  isFromDevicePerspective: boolean;
}

interface option {
  label: string;
  id: string;
}

let timeout: ReturnType<typeof setTimeout>;

export default function PairingForm(props: PairingProps) {
  const theme = useTheme();
  const [pairWarnings, setPairWarnings] = useState<string | undefined>(undefined);
  const [options, setOptions] = useState<readonly option[]>([]);
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);

  const setTimerForAutocomplete = (input: string) => {
    if (timeout) {
      // clear previous timeout if someone is still typing
      clearTimeout(timeout);
    }

    timeout = setTimeout(getSearchOptions, 100, input);
  };

  const getSearchOptions = async (input) => {
    if (input.length < 1) {
      return;
    }

    props.isFromDevicePerspective &&
      (await searchSerialNumbers(input).then((response) => {
        if (!response?.data) {
          console.error('No results', response);

          return [];
        }

        const serialNumbers = response?.data.map((p) => {
          return { label: p.serialNumber, id: p.id };
        });

        setOptions(serialNumbers);
      }));

    !props.isFromDevicePerspective &&
      (await searchDeviceIds(input).then((response) => {
        if (!response?.data) {
          console.error('No results', response);

          return [];
        }

        const serialNumbers = response?.data.map((p) => {
          return { label: p.deviceId, id: p.id };
        });

        setOptions(serialNumbers);
      }));
  };

  function onInputChange(event: any, value: string): void {
    setSearchValue(value);
    setTimerForAutocomplete(value);
  }

  function onValueChange(event: any, newValue: option | null): void {
    setOptions(newValue ? [newValue, ...options] : options);
    props.setCurrentValue(newValue);

    props.isFromDevicePerspective &&
      newValue &&
      getPairingInfoByEquipmentId(newValue?.id).then((x) => {
        if (!x.data.isEquipmentPairable) {
          props.formRef.setErrors({ serialNumber: x.data.equipmentNotPairableReasons?.map((x) => x.value).join('. ') });
        } else {
          props.formRef.setErrors({});
        }
      });
    !props.isFromDevicePerspective &&
      newValue &&
      getPairingInfoByDeviceId(newValue?.id).then((x) => {
        if (!x.data.isDevicePairable) {
          const notPairableReasons = x.data.deviceNotPairableReasons || [];
          const unhealthyReasons = x.data.deviceNotHealthyReasons || [];
          const reasons = [...notPairableReasons, ...unhealthyReasons];

          // check if we're only dealing with a failed health check
          // if so, let's check if the user is allowed to override the health check.
          // if so, show the validation errors as a warning, not as an error, so the save button is still enabled
          // and the user is able to save the pairing and override the health check.
          if (!notPairableReasons.length && unhealthyReasons.length && AuthService.hasPermission(AppPermissions.Pairing.OverrideHealthCheck)) {
            setPairWarnings(reasons?.map((x) => x.value).join('. '));
            props.formRef.setFieldError('serialNumber', undefined);
          } else {
            props.formRef.setFieldError('serialNumber', reasons?.map((x) => x.value).join('. '));
          }
        } else {
          props.formRef.setErrors({});
        }
      });
  }

  return (
    <Box sx={{ mx: 'auto', px: 0.5, py: 0.25 }}>
      <form onSubmit={props.formRef.handleSubmit}>
        <Grid container spacing={1.25}>
          <Grid xs={12} item>
            <Autocomplete
              size='small'
              filterOptions={(x) => x} // disable default filtering, because we filter in the backend
              id='serialNumber'
              options={options}
              value={props.currentValue}
              noOptionsText={props.isFromDevicePerspective ? 'No Equipment' : 'No Devices'}
              includeInputInList
              renderInput={(params) => (
                <TextField variant='outlined' {...params} label={props.isFromDevicePerspective ? 'Serial Number' : 'Device ID'} />
              )}
              onChange={onValueChange}
              onInputChange={onInputChange}
            />
          </Grid>
          <Grid xs={12} item>
            {pairWarnings && (
              <Alert
                severity='warning'
                icon={<Warning fontSize='small' />}
                sx={{ py: 0, color: theme.palette.warning.dark, '& .MuiAlert-message': { py: 0.25 } }}
              >
                {pairWarnings}
              </Alert>
            )}
          </Grid>
          <Grid xs={12} item>
            {!props.formRef.isValid && props.formRef.errors.serialNumber ? (
              <Alert
                severity='error'
                icon={<Error fontSize='small' />}
                sx={{ py: 0, color: theme.palette.error.dark, '& .MuiAlert-message': { py: 0.25 } }}
              >
                {props.formRef.errors.serialNumber}
              </Alert>
            ) : searchValue ? (
              <Alert
                severity='success'
                icon={<SvgIcon component={Check} />}
                sx={{ py: 0, color: theme.palette.success.dark, '& .MuiAlert-message': { py: 0.25 } }}
              >
                Ready to pair
              </Alert>
            ) : (
              ''
            )}
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}
